import {
  Page,
  Layout,
  Card,
  IndexTable,
  Badge,
  Button,
  Modal,
  TextField,
  Select,
  BlockStack,
  Text,
  InlineStack,
  Tabs,
  Divider,
  Checkbox,
  DropZone,
  Thumbnail,
  Banner,
  Spinner,
  useIndexResourceState,
} from '@shopify/polaris';
import { DeleteIcon, PlusIcon, ImageIcon } from '@shopify/polaris-icons';
import { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../api';
import RichTextEditor from '../components/RichTextEditor';

// ─── Types ─────────────────────────────────────────────────────

const LOCALES = [
  { code: 'es', label: '🇪🇸 Español' },
  { code: 'en', label: '🇬🇧 English' },
  { code: 'fr', label: '🇫🇷 Français' },
  { code: 'de', label: '🇩🇪 Deutsch' },
  { code: 'it', label: '🇮🇹 Italiano' },
  { code: 'pt', label: '🇵🇹 Português' },
  { code: 'nl', label: '🇳🇱 Nederlands' },
  { code: 'pl', label: '🇵🇱 Polski' },
];

const CONTENT_TYPES = [
  { label: '📝 Texto', value: 'text' },
  { label: '🎥 Video', value: 'video' },
  { label: '🖼️ Galería de imágenes', value: 'image_gallery' },
];

interface LocaleForm {
  locale: string;
  title: string;
  body: string;
  mediaUrls: string[];
  pushTitle: string;
  pushBody: string;
}

interface ContentItem {
  id: string;
  internalName: string;
  contentType: string;
  active: boolean;
  showInNews: boolean;
  newsDurationDays: number;
  newsImageUrl: string | null;
  publishedAt: string | null;
  journeyOnly: boolean;
  pushScheduledAt: string | null;
  pushSent: boolean;
  createdAt: string;
  updatedAt: string;
  locales: LocaleForm[];
}

// ─── Helpers ───────────────────────────────────────────────────

function emptyLocales(): LocaleForm[] {
  return LOCALES.map((l) => ({
    locale: l.code,
    title: '',
    body: '',
    mediaUrls: [],
    pushTitle: '',
    pushBody: '',
  }));
}

function contentTypeLabel(type: string): string {
  return CONTENT_TYPES.find((t) => t.value === type)?.label ?? type;
}

function filledLocales(locales: LocaleForm[]): string {
  const filled = locales.filter((l) => l.title.trim() || l.body.trim());
  return filled.map((l) => {
    const loc = LOCALES.find((ll) => ll.code === l.locale);
    return loc?.label.slice(0, 4) ?? l.locale;
  }).join(' ');
}

// ─── Component ─────────────────────────────────────────────────

export default function ContentLibrary() {
  const [items, setItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ContentItem | null>(null);

  // Form
  const [internalName, setInternalName] = useState('');
  const [contentType, setContentType] = useState('text');
  const [journeyOnly, setJourneyOnly] = useState(false);
  const [showInNews, setShowInNews] = useState(false);
  const [newsDurationDays, setNewsDurationDays] = useState('7');
  const [newsImageUrl, setNewsImageUrl] = useState('');
  const [newsImageUploading, setNewsImageUploading] = useState(false);
  const [newsImageError, setNewsImageError] = useState('');
  const [mediaUploading, setMediaUploading] = useState(false);
  const [pushScheduledAt, setPushScheduledAt] = useState('');
  const [locales, setLocales] = useState<LocaleForm[]>(emptyLocales());
  const [localeTab, setLocaleTab] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet<{ content: ContentItem[] }>('/panel-api/content');
      setItems(data.content);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setInternalName('');
    setContentType('text');
    setJourneyOnly(false);
    setShowInNews(false);
    setNewsDurationDays('7');
    setNewsImageUrl('');
    setNewsImageUploading(false);
    setNewsImageError('');
    setPushScheduledAt('');
    setLocales(emptyLocales());
    setLocaleTab(0);
    setShowModal(true);
  };

  const openEdit = (item: ContentItem) => {
    setEditing(item);
    setInternalName(item.internalName);
    setContentType(item.contentType);
    setJourneyOnly(item.journeyOnly);
    setShowInNews(item.showInNews);
    setNewsDurationDays(String(item.newsDurationDays));
    setNewsImageUrl(item.newsImageUrl ?? '');
    setNewsImageUploading(false);
    setNewsImageError('');
    // Format datetime-local for input
    setPushScheduledAt(item.pushScheduledAt ? new Date(item.pushScheduledAt).toISOString().slice(0, 16) : '');
    // Merge existing locales with empty ones
    const merged = LOCALES.map((l) => {
      const existing = item.locales.find((el) => el.locale === l.code);
      return existing ?? {
        locale: l.code,
        title: '',
        body: '',
        mediaUrls: [],
        pushTitle: '',
        pushBody: '',
      };
    });
    setLocales(merged);
    setLocaleTab(0);
    setShowModal(true);
  };

  const updateLocale = (idx: number, field: keyof LocaleForm, value: string | string[]) => {
    setLocales((prev) => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const addMediaUrl = (idx: number) => {
    setLocales((prev) => prev.map((l, i) =>
      i === idx ? { ...l, mediaUrls: [...l.mediaUrls, ''] } : l,
    ));
  };

  const updateMediaUrl = (localeIdx: number, urlIdx: number, value: string) => {
    setLocales((prev) => prev.map((l, i) => {
      if (i !== localeIdx) return l;
      const urls = [...l.mediaUrls];
      urls[urlIdx] = value;
      return { ...l, mediaUrls: urls };
    }));
  };

  const removeMediaUrl = (localeIdx: number, urlIdx: number) => {
    setLocales((prev) => prev.map((l, i) => {
      if (i !== localeIdx) return l;
      return { ...l, mediaUrls: l.mediaUrls.filter((_, j) => j !== urlIdx) };
    }));
  };

  const handleNewsImageUpload = useCallback(async (_dropFiles: File[], acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setNewsImageUploading(true);
    setNewsImageError('');

    try {
      // 1. Get staged upload URL
      const { stagedTarget } = await apiPost<{
        stagedTarget: {
          url: string;
          resourceUrl: string;
          parameters: Array<{ name: string; value: string }>;
        };
      }>('/panel-api/images/staged-upload', {
        fileName: file.name,
        mimeType: file.type || 'image/jpeg',
        fileSize: String(file.size),
      });

      // 2. Upload to Shopify staged URL
      const formData = new FormData();
      for (const param of stagedTarget.parameters) {
        formData.append(param.name, param.value);
      }
      formData.append('file', file);

      const uploadRes = await fetch(stagedTarget.url, {
        method: 'POST',
        body: formData,
      });

      if (!uploadRes.ok) {
        throw new Error(`Upload failed: ${uploadRes.status}`);
      }

      // 3. Register in Shopify Files to get CDN URL
      const { imageUrl } = await apiPost<{ fileId: string; imageUrl: string }>(
        '/panel-api/images/register',
        {
          resourceUrl: stagedTarget.resourceUrl,
          fileName: file.name,
        },
      );

      setNewsImageUrl(imageUrl);
    } catch (err: any) {
      console.error('Image upload failed:', err);
      setNewsImageError(err.message || 'Error al subir imagen');
    } finally {
      setNewsImageUploading(false);
    }
  }, []);

  /** Upload a media file (image or video) via Shopify staged uploads and add URL to locale mediaUrls */
  const handleMediaUpload = useCallback(async (_dropFiles: File[], acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    setMediaUploading(true);

    try {
      for (const file of acceptedFiles) {
        // 1. Get staged upload URL
        const isVideo = file.type.startsWith('video/');
        const { stagedTarget } = await apiPost<{
          stagedTarget: {
            url: string;
            resourceUrl: string;
            parameters: Array<{ name: string; value: string }>;
          };
        }>('/panel-api/images/staged-upload', {
          fileName: file.name,
          mimeType: file.type || (isVideo ? 'video/mp4' : 'image/jpeg'),
          fileSize: String(file.size),
          resource: isVideo ? 'VIDEO' : 'IMAGE',
        });

        // 2. Upload to Shopify staged URL
        const formData = new FormData();
        for (const param of stagedTarget.parameters) {
          formData.append(param.name, param.value);
        }
        formData.append('file', file);

        const uploadRes = await fetch(stagedTarget.url, {
          method: 'POST',
          body: formData,
        });

        if (!uploadRes.ok) {
          throw new Error(`Upload failed: ${uploadRes.status}`);
        }

        // 3. Register in Shopify Files to get CDN URL
        const { imageUrl } = await apiPost<{ fileId: string; imageUrl: string }>(
          '/panel-api/images/register',
          {
            resourceUrl: stagedTarget.resourceUrl,
            fileName: file.name,
          },
        );

        // 4. Add to current locale's mediaUrls
        setLocales((prev) =>
          prev.map((l, i) =>
            i === localeTab ? { ...l, mediaUrls: [...l.mediaUrls, imageUrl] } : l,
          ),
        );
      }
    } catch (err: any) {
      console.error('Media upload failed:', err);
      alert(`Error al subir archivo: ${err.message || 'Error desconocido'}`);
    } finally {
      setMediaUploading(false);
    }
  }, [localeTab]);

  const handleSave = async () => {
    const payload = {
      internalName,
      contentType,
      journeyOnly,
      showInNews: journeyOnly ? false : showInNews,
      newsDurationDays: parseInt(newsDurationDays, 10) || 7,
      newsImageUrl: newsImageUrl.trim() || null,
      pushScheduledAt: pushScheduledAt ? new Date(pushScheduledAt).toISOString() : null,
      locales: locales.filter((l) =>
        l.title.trim() || l.body.trim() || l.mediaUrls.some((u) => u.trim()) ||
        l.pushTitle.trim() || l.pushBody.trim()
      ),
    };

    try {
      if (editing) {
        await apiPut(`/panel-api/content/${editing.id}`, payload);
      } else {
        await apiPost('/panel-api/content', payload);
      }
      setShowModal(false);
      await fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const [deleteChecking, setDeleteChecking] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    setDeleteChecking(id);
    try {
      // Check if can delete
      const check = await apiGet<{ canDelete: boolean }>(`/panel-api/content/${id}/can-delete`);
      if (!check.canDelete) {
        alert('No se puede eliminar: este contenido está referenciado en un journey activo.');
        return;
      }
      if (!confirm('¿Eliminar este contenido permanentemente? Esta acción no se puede deshacer.')) return;
      await apiDelete(`/panel-api/content/${id}`);
      await fetchData();
    } catch (err: any) {
      console.error(err);
      alert(err.message || 'Error al eliminar');
    } finally {
      setDeleteChecking(null);
    }
  };

  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(items as any);

  const currentLocale = locales[localeTab];
  const mediaLabel = contentType === 'video' ? 'Video URL' : contentType === 'image_gallery' ? 'Image URLs' : '';

  return (
    <Page
      title="Content Library"
      subtitle="Contenido reutilizable multi-idioma para journeys"
      primaryAction={{ content: 'Crear contenido', onAction: openCreate, icon: PlusIcon }}
    >
      <Layout>
        <Layout.Section>
          <Card padding="0">
            <IndexTable
              resourceName={{ singular: 'content', plural: 'contents' }}
              itemCount={items.length}
              selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
              onSelectionChange={handleSelectionChange}
              headings={[
                { title: 'Nombre' },
                { title: 'Tipo' },
                { title: 'Idiomas' },
                { title: 'Novedades' },
                { title: 'Estado' },
                { title: 'Acciones' },
              ]}
              loading={loading}
            >
              {items.map((item, idx) => (
                <IndexTable.Row
                  id={item.id}
                  key={item.id}
                  position={idx}
                  selected={selectedResources.includes(item.id)}
                >
                  <IndexTable.Cell>
                    <Text as="span" variant="bodyMd" fontWeight="bold">
                      {item.internalName}
                    </Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>{contentTypeLabel(item.contentType)}</IndexTable.Cell>
                  <IndexTable.Cell>{filledLocales(item.locales)}</IndexTable.Cell>
                  <IndexTable.Cell>
                    {item.journeyOnly ? (
                      <Badge tone="warning">🔗 Journey</Badge>
                    ) : item.showInNews ? (
                      <Badge tone="info">
                        {`📢 ${item.newsDurationDays}d`}
                      </Badge>
                    ) : (
                      <Text as="span" tone="subdued">—</Text>
                    )}
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Badge tone={item.active ? 'success' : undefined}>
                      {item.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <InlineStack gap="200">
                      <Button size="slim" onClick={() => openEdit(item)}>Editar</Button>
                      <Button
                        size="slim"
                        tone="critical"
                        icon={DeleteIcon}
                        onClick={() => handleDelete(item.id)}
                        loading={deleteChecking === item.id}
                      >
                        Eliminar
                      </Button>
                    </InlineStack>
                  </IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          </Card>
        </Layout.Section>
      </Layout>

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? 'Editar contenido' : 'Nuevo contenido'}
        primaryAction={{ content: 'Guardar', onAction: handleSave }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setShowModal(false) }]}
        size="large"
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Nombre interno"
              value={internalName}
              onChange={setInternalName}
              autoComplete="off"
              helpText="Solo visible en el admin, para identificar el contenido"
            />
            <Select
              label="Tipo de contenido"
              options={CONTENT_TYPES}
              value={contentType}
              onChange={setContentType}
            />

            <Divider />

            <Checkbox
              label="Solo para Journeys"
              checked={journeyOnly}
              onChange={(v) => {
                setJourneyOnly(v);
                if (v) setShowInNews(false);
              }}
              helpText="Si está activado, este contenido solo se mostrará dentro de Journeys (no aparecerá en Novedades)"
            />

            {!journeyOnly && (
              <>
                <Text as="h3" variant="headingSm">📢 Novedades</Text>

                <Checkbox
                  label="Mostrar en sección Novedades"
                  checked={showInNews}
                  onChange={setShowInNews}
                  helpText="Si está activado, este contenido aparecerá en la sección de Novedades de la app durante el periodo especificado"
                />
              </>
            )}

            {showInNews && !journeyOnly && (
              <>
                <TextField
                  label="Duración (días)"
                  type="number"
                  value={newsDurationDays}
                  onChange={setNewsDurationDays}
                  autoComplete="off"
                  helpText="Número de días que se mostrará en Novedades (por defecto 7)"
                  min={"1"}
                  max={"365"}
                />
                <BlockStack gap="200">
                  <Text as="span" variant="bodyMd" fontWeight="semibold">Imagen destacada</Text>
                  {newsImageError && (
                    <Banner tone="critical" onDismiss={() => setNewsImageError('')}>
                      <p>{newsImageError}</p>
                    </Banner>
                  )}
                  {newsImageUrl ? (
                    <InlineStack gap="300" blockAlign="center">
                      <Thumbnail source={newsImageUrl} alt="Imagen destacada" size="large" />
                      <Button variant="plain" tone="critical" onClick={() => setNewsImageUrl('')}>
                        Eliminar imagen
                      </Button>
                    </InlineStack>
                  ) : (
                    <DropZone
                      accept="image/*"
                      type="image"
                      onDrop={handleNewsImageUpload}
                      allowMultiple={false}
                      disabled={newsImageUploading}
                    >
                      {newsImageUploading ? (
                        <div style={{ textAlign: 'center', padding: 20 }}>
                          <Spinner size="small" />
                          <Text as="p" variant="bodySm">Subiendo imagen...</Text>
                        </div>
                      ) : (
                        <DropZone.FileUpload actionHint="o arrastra una imagen" />
                      )}
                    </DropZone>
                  )}
                </BlockStack>
              </>
            )}

            <Divider />
            <Text as="h3" variant="headingSm">Contenido por idioma</Text>

            <Tabs
              tabs={LOCALES.map((l, i) => ({
                id: `locale-${l.code}`,
                content: l.label,
                badge: locales[i]?.title?.trim() ? '✓' : undefined,
              }))}
              selected={localeTab}
              onSelect={setLocaleTab}
            >
              {currentLocale && (
                <div style={{ paddingTop: 12 }}>
                  <BlockStack gap="300">
                    <TextField
                      label="Título"
                      value={currentLocale.title}
                      onChange={(v) => updateLocale(localeTab, 'title', v)}
                      autoComplete="off"
                    />
                    {contentType === 'text' ? (
                      <RichTextEditor
                        label="Contenido"
                        value={currentLocale.body}
                        onChange={(v) => updateLocale(localeTab, 'body', v)}
                      />
                    ) : (
                      <TextField
                        label="Descripción"
                        value={currentLocale.body}
                        onChange={(v) => updateLocale(localeTab, 'body', v)}
                        multiline={3}
                        autoComplete="off"
                      />
                    )}

                    {/* Media section for video / image_gallery */}
                    {contentType !== 'text' && (
                      <>
                        <Divider />
                        <Text as="h4" variant="headingSm">
                          {contentType === 'video' ? '🎥 Video' : '🖼️ Imágenes'}
                        </Text>

                        {/* Existing media */}
                        {currentLocale.mediaUrls.map((url, urlIdx) => (
                          <InlineStack key={urlIdx} gap="300" blockAlign="center">
                            {url && !url.startsWith('http') ? null : contentType === 'video' ? (
                              <div style={{ width: 80, height: 60, backgroundColor: '#f0f0f0', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <span style={{ fontSize: 24 }}>🎥</span>
                              </div>
                            ) : url ? (
                              <Thumbnail source={url} alt={`Media ${urlIdx + 1}`} size="medium" />
                            ) : null}
                            <div style={{ flex: 1 }}>
                              <TextField
                                label={contentType === 'video' ? `Video URL` : `Imagen ${urlIdx + 1}`}
                                value={url}
                                onChange={(v) => updateMediaUrl(localeTab, urlIdx, v)}
                                autoComplete="off"
                                placeholder="https://..."
                                labelHidden={!!url}
                              />
                            </div>
                            <Button
                              icon={DeleteIcon}
                              tone="critical"
                              variant="plain"
                              onClick={() => removeMediaUrl(localeTab, urlIdx)}
                            />
                          </InlineStack>
                        ))}

                        {/* Upload zone */}
                        <DropZone
                          accept={contentType === 'video' ? 'video/*' : 'image/*'}
                          type={contentType === 'video' ? 'file' : 'image'}
                          onDrop={handleMediaUpload}
                          allowMultiple={contentType === 'image_gallery'}
                          disabled={mediaUploading || (contentType === 'video' && currentLocale.mediaUrls.length > 0)}
                        >
                          {mediaUploading ? (
                            <div style={{ textAlign: 'center', padding: 20 }}>
                              <Spinner size="small" />
                              <Text as="p" variant="bodySm">Subiendo...</Text>
                            </div>
                          ) : (
                            <DropZone.FileUpload
                              actionHint={contentType === 'video'
                                ? 'o arrastra un video'
                                : 'o arrastra imágenes'}
                            />
                          )}
                        </DropZone>

                        {/* Manual URL option */}
                        {(contentType === 'image_gallery' || currentLocale.mediaUrls.length === 0) && (
                          <Button
                            variant="plain"
                            icon={PlusIcon}
                            onClick={() => addMediaUrl(localeTab)}
                          >
                            Añadir URL manualmente
                          </Button>
                        )}
                      </>
                    )}

                    <Divider />
                    <Text as="p" variant="bodySm" tone="subdued">Push notification (opcional)</Text>
                    <TextField
                      label="Push título"
                      value={currentLocale.pushTitle}
                      onChange={(v) => updateLocale(localeTab, 'pushTitle', v)}
                      autoComplete="off"
                    />
                    <TextField
                      label="Push cuerpo"
                      value={currentLocale.pushBody}
                      onChange={(v) => updateLocale(localeTab, 'pushBody', v)}
                      autoComplete="off"
                    />

                    {localeTab === 0 && (
                      <>
                        <Divider />
                        <Text as="p" variant="bodySm" fontWeight="semibold">⏰ Programar envío de push</Text>
                        <div>
                          <label style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>
                            Fecha y hora de envío
                          </label>
                          <input
                            type="datetime-local"
                            value={pushScheduledAt}
                            onChange={(e) => setPushScheduledAt(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: '1px solid #ccc',
                              borderRadius: 8,
                              fontSize: 14,
                              fontFamily: 'inherit',
                            }}
                          />
                          <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                            {editing?.pushSent
                              ? '✅ Push ya enviado'
                              : pushScheduledAt
                                ? `Se enviará el ${new Date(pushScheduledAt).toLocaleString('es-ES')}`
                                : 'Deja vacío para no enviar push'}
                          </p>
                        </div>
                      </>
                    )}
                  </BlockStack>
                </div>
              )}
            </Tabs>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
